'use client';

import { Container, Title, Text, Button, Group, Stack, Grid, Card, ThemeIcon, Box, Badge, Anchor, SimpleGrid } from '@mantine/core';
import { IconRocket, IconCode, IconShield, IconUsers, IconChartBar, IconCloud, IconCheck, IconArrowRight } from '@tabler/icons-react';
import Link from 'next/link';
import { AppShellComponent } from '@/components/AppShellComponent';

const features = [
  {
    icon: IconRocket,
    title: 'Lightning Fast',
    description: 'Built for speed and performance. Get started in minutes, not hours.',
  },
  {
    icon: IconShield,
    title: 'Enterprise Security',
    description: 'Bank-level security with end-to-end encryption and compliance certifications.',
  },
  {
    icon: IconUsers,
    title: 'Team Collaboration',
    description: 'Work together seamlessly with real-time updates and intuitive sharing.',
  },
  {
    icon: IconChartBar,
    title: 'Advanced Analytics',
    description: 'Gain insights with powerful analytics and customizable dashboards.',
  },
  {
    icon: IconCloud,
    title: 'Cloud Native',
    description: 'Scalable infrastructure that grows with your business needs.',
  },
  {
    icon: IconCode,
    title: 'API First',
    description: 'Integrate with your existing tools using our comprehensive API.',
  },
];

const pricingPlans = [
  {
    name: 'Starter',
    price: '$29',
    period: '/month',
    description: 'Perfect for small teams',
    features: ['Up to 10 users', '5GB storage', 'Basic analytics', 'Email support'],
    highlighted: false,
  },
  {
    name: 'Professional',
    price: '$99',
    period: '/month',
    description: 'For growing businesses',
    features: ['Up to 50 users', '100GB storage', 'Advanced analytics', 'Priority support', 'Custom integrations'],
    highlighted: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'Tailored for large organizations',
    features: ['Unlimited users', 'Unlimited storage', 'Enterprise analytics', '24/7 phone support', 'Custom development'],
    highlighted: false,
  },
];

export default function HomePage() {
  return (
    <AppShellComponent>
      <Box>
        {/* Hero Section */}
        <Box py={80} style={{ backgroundColor: 'var(--mantine-color-blue-light)' }}>
          <Container size="lg">
            <Stack align="center" gap="xl">
              <Badge size="lg" variant="filled" radius="sm">
                Trusted by 10,000+ teams worldwide
              </Badge>

              <Title order={1} size={48} ta="center" style={{ maxWidth: 800 }}>
                Streamline Your Business with{' '}
                <Text span c="blue" inherit>
                  CoreProcess
                </Text>
              </Title>

              <Text size="xl" ta="center" c="dimmed" style={{ maxWidth: 600 }}>
                The all-in-one platform that empowers teams to collaborate, automate workflows,
                and scale their operations effortlessly.
              </Text>

              <Group>
                <Button size="lg" component={Link} href="/register" rightSection={<IconArrowRight size={20} />}>
                  Start Free Trial
                </Button>
                <Button size="lg" variant="outline" component={Link} href="/demo">
                  Watch Demo
                </Button>
              </Group>
            </Stack>
          </Container>
        </Box>

        {/* Features Section */}
        <Container size="lg" py={80}>
          <Stack gap="xl">
            <Stack align="center" gap="md">
              <Title order={2} size={36} ta="center">
                Everything you need to succeed
              </Title>
              <Text size="lg" c="dimmed" ta="center" style={{ maxWidth: 600 }}>
                Powerful features designed to help your team work more efficiently and deliver better results.
              </Text>
            </Stack>

            <Grid mt="xl">
              {features.map((feature) => (
                <Grid.Col key={feature.title} span={{ base: 12, sm: 6, md: 4 }}>
                  <Card shadow="sm" padding="lg" radius="md" withBorder>
                    <ThemeIcon size={50} radius="md" variant="light" mb="md">
                      <feature.icon size={28} stroke={1.5} />
                    </ThemeIcon>
                    <Title order={3} size="h4" mb="xs">
                      {feature.title}
                    </Title>
                    <Text c="dimmed" size="sm">
                      {feature.description}
                    </Text>
                  </Card>
                </Grid.Col>
              ))}
            </Grid>
          </Stack>
        </Container>

        {/* Pricing Section */}
        <Box py={80} style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
          <Container size="lg">
            <Stack gap="xl">
              <Stack align="center" gap="md">
                <Title order={2} size={36} ta="center">
                  Simple, transparent pricing
                </Title>
                <Text size="lg" c="dimmed" ta="center" style={{ maxWidth: 600 }}>
                  Choose the perfect plan for your team. All plans include a 14-day free trial.
                </Text>
              </Stack>

              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg" mt="xl">
                {pricingPlans.map((plan) => (
                  <Card
                    key={plan.name}
                    shadow={plan.highlighted ? 'xl' : 'sm'}
                    padding="xl"
                    radius="md"
                    withBorder
                    style={plan.highlighted ? { borderColor: 'var(--mantine-color-blue-filled)', borderWidth: 2 } : {}}
                  >
                      {plan.highlighted && (
                        <Badge variant="filled" mb="md">
                          Most Popular
                        </Badge>
                      )}
                      <Title order={3} mb="xs">
                        {plan.name}
                      </Title>
                      <Group align="baseline" gap={4} mb="xs">
                        <Text size="lg" fw={700}>
                          {plan.price}
                        </Text>
                        <Text size="lg" c="dimmed">
                          {plan.period}
                        </Text>
                      </Group>
                      <Text c="dimmed" size="sm" mb="xl">
                        {plan.description}
                      </Text>
                      <Stack gap="xs" mb="xl">
                        {plan.features.map((feature) => (
                          <Group key={feature} gap="xs">
                            <ThemeIcon size={20} radius="xl" color="teal">
                              <IconCheck size={14} stroke={3} />
                            </ThemeIcon>
                            <Text size="sm">{feature}</Text>
                          </Group>
                        ))}
                      </Stack>
                      <Button
                        fullWidth
                        variant={plan.highlighted ? 'filled' : 'outline'}
                        component={Link}
                        href="/register"
                      >
                        Get Started
                      </Button>
                  </Card>
                ))}
              </SimpleGrid>
            </Stack>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box py={80}>
          <Container size="lg">
            <Card shadow="xl" padding="xl" radius="md" style={{ backgroundColor: 'var(--mantine-color-blue-filled)', color: 'white' }}>
              <Stack align="center" gap="xl">
                <Title order={2} size={36} ta="center" c="white">
                  Ready to transform your business?
                </Title>
                <Text size="lg" ta="center" c="white" opacity={0.9} style={{ maxWidth: 600 }}>
                  Join thousands of teams already using CoreProcess to streamline their operations
                  and accelerate growth.
                </Text>
                <Group>
                  <Button size="lg" variant="white" color="blue" component={Link} href="/register">
                    Start Free Trial
                  </Button>
                  <Button size="lg" variant="outline" color="white" component={Link} href="/contact">
                    Contact Sales
                  </Button>
                </Group>
              </Stack>
            </Card>
          </Container>
        </Box>

        {/* Footer */}
        <Box py={40} style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
          <Container size="lg">
            <Group justify="space-between">
              <Text c="dimmed" size="sm">
                © 2024 CoreProcess. All rights reserved.
              </Text>
              <Group gap="lg">
                <Anchor href="/privacy" size="sm" c="dimmed">
                  Privacy Policy
                </Anchor>
                <Anchor href="/terms" size="sm" c="dimmed">
                  Terms of Service
                </Anchor>
                <Anchor href="/contact" size="sm" c="dimmed">
                  Contact
                </Anchor>
              </Group>
            </Group>
          </Container>
        </Box>
      </Box>
    </AppShellComponent>
  );
}
